import { Avatar, Button, Col, Collapse, Divider, List } from 'antd';
import Deviations from './Deviations/Deviations';
import React, { useContext, useState } from 'react';
import { DeviationType, NodeType } from '../../../types/types';
import { useDispatch } from 'react-redux';
import { getAllDeviationsByNodeId } from '../../../features/projects/deviationSlice';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleFilled,
  EyeOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { getProjectTreeView } from '../../../features/projects/projectSlice';
import { NotificationContext } from '../../../providers/NotificationProvider';

// Definición del tipo de Props
interface Props {
  nodes: NodeType[];
  handleEdit: (node: NodeType) => void;
  handleDelete: (node: NodeType) => void;
  onSelectionChange?: (type: string, id: number) => void;
  handleMoveUp: (node: NodeType) => void;
  handleMoveDown: (node: NodeType) => void;
  handleClone: (node: NodeType) => void;
}

const NodeList: React.FC<Props> = ({
  nodes,
  handleEdit,
  handleDelete,
  onSelectionChange,
  handleMoveUp,
  handleMoveDown,
  handleClone,
}) => {
  const dispatch = useDispatch();
  const { project_id } = useParams();
  const [activeNodeId, setActiveNodeId] = useState<number | null>(null);
  const [deviationsByNode, setDeviationsByNode] = useState<Record<number, DeviationType[]>>({});
  const { triggerNotification } = useContext(NotificationContext);

  const toggleCollapse = (nodeId: number) => {
    const willOpen = activeNodeId !== nodeId;
    setActiveNodeId(willOpen ? nodeId : null);
    if (willOpen) {
      loadDeviations(nodeId);
    }
  };

  const getProjectTree = () => {
    dispatch(getProjectTreeView(parseInt(project_id as string)) as any)
      .then((result: any) => {
        if (getProjectTreeView.fulfilled.match(result)) {
          triggerNotification('success', 'Exito', 'Se cargó el árbol del proyecto correctamente', 'bottomRight');
        } else {
          triggerNotification('error', 'Error', 'Error al cargar el árbol del proyecto', 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', 'Error', 'Error al cargar el árbol del proyecto', 'bottomRight');
      });
  };

  const loadDeviations = (nodeId: number) =>
    dispatch(getAllDeviationsByNodeId(nodeId) as any)
      .then((result: any) => {
        if (getAllDeviationsByNodeId.fulfilled.match(result)) {
          // Aquí puedes manejar las desviaciones cargadas
          setDeviationsByNode((prev) => ({ ...prev, [nodeId]: result.payload }));
          triggerNotification('success', 'Exito', 'Se cargaron las desviaciones correctamente', 'bottomRight');
        } else {
          triggerNotification('error', 'Error', 'Error al cargar las desviaciones', 'bottomRight');
        }
      })
      .catch((err: any) => {
        triggerNotification('error', 'Error', 'Error al cargar las desviaciones', 'bottomRight');
      });

  return (
    <List
      itemLayout='horizontal'
      dataSource={nodes}
      renderItem={(node: NodeType, index: number) => {
        const nid = node.id ?? 0;
        const isOpen = activeNodeId === nid;
        const deviationsForNode = deviationsByNode[nid] ?? [];

        // límites para las flechas
        const isFirst = index === 0;
        const isLast = index === nodes.length - 1;

        return (
          <React.Fragment key={nid || index}>
            <List.Item
              actions={[
                <Button
                  key='toggle'
                  type='default'
                  icon={isOpen ? <EyeInvisibleFilled /> : <EyeOutlined />}
                  onClick={() => toggleCollapse(nid)}
                  style={{ marginLeft: 10 }}
                ></Button>,
                <Button key='edit' type='primary' icon={<EditOutlined />} onClick={() => handleEdit(node)}></Button>,
                <Button key='clone' type='default' icon={<CopyOutlined />} onClick={() => handleClone(node)}></Button>,
                <Button
                  key='up'
                  type='default'
                  icon={<ArrowUpOutlined />}
                  onClick={() => handleMoveUp(node)}
                  disabled={isFirst}
                />,
                <Button
                  key='down'
                  type='default'
                  icon={<ArrowDownOutlined />}
                  onClick={() => handleMoveDown(node)}
                  disabled={isLast}
                />,
                <Button
                  key='delete'
                  type='primary'
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(node)}
                  style={{ marginLeft: 10 }}
                ></Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    style={{
                      backgroundColor: node.color ?? '#000000',
                      color: node.color === '#FFFFFF' ? '#000000' : '#ffffff',
                      borderColor: node.color === '#FFFFFF' ? '#000000' : 'transparent',
                    }}
                  >
                    {index + 1}
                  </Avatar>
                }
                title={
                  <button
                    type='button'
                    onClick={() => onSelectionChange?.('node', nid)}
                    style={{
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: '#000',
                      textDecoration: 'none',
                      font: 'inherit',
                    }}
                  >
                    {node.name}
                  </button>
                }
                description={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: (node.description || '').replace(/\n/g, '<br />'),
                    }}
                  />
                }
              />
            </List.Item>

            {isOpen && (
              <Collapse activeKey={String(nid)} style={{ padding: 5 }}>
                <Col className='node-content-container'>
                  {node.design_intention && (
                    <div>
                      <b>Intención del Diseño:</b>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: node.design_intention?.replace(/\n/g, '<br />') || 'No disponible',
                        }}
                      />
                    </div>
                  )}
                  {node.special_consideration && (
                    <div>
                      <b>P&ID:</b>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: node.special_consideration?.replace(/\n/g, '<br />') || 'No disponible',
                        }}
                      />
                    </div>
                  )}
                  {node.comments && (
                    <p>
                      <b>Comentarios:</b>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: node.comments?.replace(/\n/g, '<br />') || 'No disponible',
                        }}
                      />
                    </p>
                  )}
                  <Divider />
                </Col>
                <br />
                <Col span={24} className='node-content-container'>
                  <Deviations
                    nodeId={nid}
                    nodeOrder={index + 1}
                    deviations={deviationsForNode}
                    getDeviations={() => loadDeviations(nid)}
                    onSelectionChange={onSelectionChange}
                    updateTree={() => getProjectTree()}
                  />
                </Col>
              </Collapse>
            )}
          </React.Fragment>
        );
      }}
    />
  );
};

export default NodeList;
